/**
 * LoadingSpinner Component
 *
 * Consistent loading indicator used across dashboard pages.
 * Supports two modes:
 * - Full-page: centered spinner with optional message (default)
 * - Inline: small spinner for use inside buttons or compact areas
 *
 * @component
 * @param {Object} props
 * @param {'sm'|'md'|'lg'} [props.size] - Spinner size (default: 'lg')
 * @param {string} [props.message] - Optional loading text below spinner
 * @param {string} [props.color] - Border color class (default: 'border-blue-600')
 * @param {string} [props.className] - Additional wrapper classes
 */
import PropTypes from 'prop-types';

const SIZE_CLASSES = {
  sm: 'h-4 w-4',
  md: 'h-8 w-8',
  lg: 'h-12 w-12',
};

export default function LoadingSpinner({
  size = 'lg',
  message,
  color = 'border-blue-600',
  className = '',
}) {
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.lg;

  const spinner = (
    <div className={`animate-spin rounded-full border-b-2 ${color} ${sizeClass}`} />
  );

  // Inline mode: no wrapper, no message
  if (!message && size === 'sm') {
    return spinner;
  }

  return (
    <div className={`flex items-center justify-center ${className}`.trim()}>
      <div className="text-center">
        {size === 'lg' ? <div className={`animate-spin rounded-full border-b-2 ${color} ${sizeClass} mx-auto`} /> : spinner}
        {message && <p className="mt-4 text-gray-600">{message}</p>}
      </div>
    </div>
  );
}

LoadingSpinner.propTypes = {
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  message: PropTypes.string,
  color: PropTypes.string,
  className: PropTypes.string,
};
